import React, { useState, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Camera, Sparkles, Upload, X } from 'lucide-react';
import { Button } from './Button';
interface AvatarPickerProps {
  currentAvatar: string;
  onSelect: (avatarUrl: string) => void;
  onClose: () => void;
}
const avatarStyles = ['adventurer', 'avataaars', 'big-smile', 'bottts', 'croodles', 'fun-emoji', 'lorelei', 'micah', 'miniavs', 'open-peeps', 'personas', 'pixel-art'];
const seeds = ['Felix', 'Aneka', 'Jasmine', 'Luna', 'Max', 'Oliver', 'Sophie', 'Charlie', 'Milo', 'Lucy', 'Leo', 'Bella', 'Jack', 'Lily', 'Oscar', 'Chloe'];
export function AvatarPicker({
  currentAvatar,
  onSelect,
  onClose
}: AvatarPickerProps) {
  const [selectedStyle, setSelectedStyle] = useState('avataaars');
  const [customSeed, setCustomSeed] = useState('');
  const [uploadedImage, setUploadedImage] = useState<string | null>(null);
  const generateAvatarUrl = (style: string, seed: string) => {
    return `https://api.dicebear.com/7.x/${style}/svg?seed=${encodeURIComponent(seed)}`;
  };
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setUploadedImage(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };
  const handleSelectAvatar = (url: string) => {
    onSelect(url);
    onClose();
  };
  return <AnimatePresence data-id="element-114">
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-6" onClick={onClose} data-id="element-115">
        <motion.div initial={{
        scale: 0.95,
        y: 20
      }} animate={{
        scale: 1,
        y: 0
      }} exit={{
        scale: 0.95,
        y: 20
      }} className="bg-white dark:bg-[#252936] rounded-3xl p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()} data-id="element-116">
          {/* Header */}
          <div className="flex items-center justify-between mb-6" data-id="element-117">
            <div data-id="element-118">
              <h2 className="text-2xl font-fredoka font-bold text-game-dark dark:text-white" data-id="element-119">
                Choose Your Avatar
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 font-quicksand" data-id="element-120">
                Select a style and customize your avatar
              </p>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-colors" data-id="element-121">
              <X className="w-6 h-6 text-gray-500 dark:text-gray-400" data-id="element-122" />
            </button>
          </div>

          {/* Upload Custom Image */}
          <div className="mb-6" data-id="element-123">
            <label className="block text-sm font-quicksand font-bold text-gray-700 dark:text-gray-300 mb-3" data-id="element-124">
              Upload Custom Image
            </label>
            <div className="flex gap-3" data-id="element-125">
              <label className="flex-1 cursor-pointer" data-id="element-126">
                <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-2xl p-6 hover:border-game-purple transition-colors text-center" data-id="element-127">
                  <Upload className="w-8 h-8 mx-auto mb-2 text-gray-400" data-id="element-128" />
                  <p className="text-sm font-quicksand text-gray-600 dark:text-gray-400" data-id="element-129">
                    Click to upload image
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-500 mt-1" data-id="element-130">
                    PNG, JPG up to 5MB
                  </p>
                </div>
                <input type="file" accept="image/*" onChange={handleFileUpload} className="hidden" data-id="element-131" />
              </label>
              {uploadedImage && <div className="relative" data-id="element-132">
                  <img src={uploadedImage} alt="Uploaded" className="w-24 h-24 rounded-2xl object-cover border-2 border-game-purple" data-id="element-133" />
                  <Button size="sm" onClick={() => handleSelectAvatar(uploadedImage)} className="mt-2 w-24" data-id="element-134">
                    Use This
                  </Button>
                </div>}
            </div>
          </div>

          {/* Avatar Style Selector */}
          <div className="mb-6" data-id="element-135">
            <label className="block text-sm font-quicksand font-bold text-gray-700 dark:text-gray-300 mb-3" data-id="element-136">
              <Sparkles className="w-4 h-4 inline mr-2" data-id="element-137" />
              Avatar Style
            </label>
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3" data-id="element-138">
              {avatarStyles.map(style => <button key={style} onClick={() => setSelectedStyle(style)} className={`p-3 rounded-xl border-2 transition-all ${selectedStyle === style ? 'border-game-purple bg-game-bg-purple' : 'border-gray-200 dark:border-gray-700 hover:border-game-purple/50'}`} data-id="element-139">
                  <img src={generateAvatarUrl(style, 'preview')} alt={style} className="w-full aspect-square rounded-lg mb-2" data-id="element-140" />
                  <p className="text-xs font-quicksand text-center text-gray-600 dark:text-gray-400 capitalize" data-id="element-141">
                    {style.replace('-', ' ')}
                  </p>
                </button>)}
            </div>
          </div>

          {/* Custom Seed Input */}
          <div className="mb-6" data-id="element-142">
            <label className="block text-sm font-quicksand font-bold text-gray-700 dark:text-gray-300 mb-3" data-id="element-143">
              Custom Name/Seed
            </label>
            <div className="flex gap-3" data-id="element-144">
              <input type="text" value={customSeed} onChange={e => setCustomSeed(e.target.value)} placeholder="Enter your name or any text..." className="flex-1 px-4 py-3 rounded-xl border-2 border-gray-200 dark:border-gray-700 bg-white dark:bg-[#252936] text-game-dark dark:text-white font-quicksand focus:outline-none focus:border-game-purple" data-id="element-145" />
              {customSeed && <div className="relative" data-id="element-146">
                  <img src={generateAvatarUrl(selectedStyle, customSeed)} alt="Custom" className="w-16 h-16 rounded-xl border-2 border-game-purple" data-id="element-147" />
                  <Button size="sm" onClick={() => handleSelectAvatar(generateAvatarUrl(selectedStyle, customSeed))} className="mt-2 w-16 text-xs" data-id="element-148">
                    Use
                  </Button>
                </div>}
            </div>
          </div>

          {/* Preset Avatars */}
          <div data-id="element-149">
            <label className="block text-sm font-quicksand font-bold text-gray-700 dark:text-gray-300 mb-3" data-id="element-150">
              Quick Select
            </label>
            <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-3" data-id="element-151">
              {seeds.map(seed => {
              const avatarUrl = generateAvatarUrl(selectedStyle, seed);
              return <button key={seed} onClick={() => handleSelectAvatar(avatarUrl)} className="group relative" data-id="element-152">
                    <img src={avatarUrl} alt={seed} className="w-full aspect-square rounded-xl border-2 border-gray-200 dark:border-gray-700 group-hover:border-game-purple transition-all group-hover:scale-105" data-id="element-153" />
                    <div className="absolute inset-0 bg-game-purple/0 group-hover:bg-game-purple/10 rounded-xl transition-colors flex items-center justify-center" data-id="element-154">
                      <Camera className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-155" />
                    </div>
                  </button>;
            })}
            </div>
          </div>

          {/* Footer */}
          <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-800" data-id="element-156">
            <Button variant="secondary" onClick={onClose} className="w-full" data-id="element-157">
              Cancel
            </Button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>;
}